import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Printer, ExternalLink, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api, { money, downloadDoc, openDocPrint, apiErr } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, Stat, AdminHeading, Th, Td, adminInput } from "@/components/admin/ui";

const PRESETS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["this_week", "This Week"], ["last_week", "Last Week"],
  ["this_month", "This Month"], ["last_month", "Last Month"], ["current_quarter", "Current Quarter"],
  ["previous_quarter", "Previous Quarter"], ["ytd", "Year to Date"], ["previous_year", "Previous Year"], ["custom", "Custom Range"],
];
const CAT_LABELS = { financial: "Financial", operational: "Operational", guest: "Guest", incident: "Incident", audit: "Audit" };
const FIN_TIP = {
  booked: "Total value of reservations booked (not necessarily paid).",
  collected: "Payments actually received. Never includes unpaid, complimentary, or waived amounts.",
  outstanding: "Balance still owed on active reservations.",
  complimentary: "Value of complimentary stays — never counted as collected revenue.",
  waived: "Amounts formally waived — never counted as collected revenue.",
  refunded: "Amounts refunded — counted once, never subtracted twice.",
  deposits: "Deposits received (a subset of collected).",
};

export default function AdminReports() {
  const nav = useNavigate();
  const [preset, setPreset] = useState("this_month");
  const [range, setRange] = useState({ start: "", end: "" });
  const [dash, setDash] = useState(null);
  const [list, setList] = useState(null);
  const [canExport, setCanExport] = useState(false);
  const [active, setActive] = useState(null);
  const [report, setReport] = useState(null);

  const qs = () => `preset=${preset}${preset === "custom" ? `&start=${range.start}&end=${range.end}` : ""}`;
  const loadDash = () => api.get(`/admin/reports/dashboard?${qs()}`).then((r) => setDash(r.data)).catch((e) => { setDash(false); toast.error(apiErr(e)); });
  useEffect(() => { api.get("/admin/reports/list").then((r) => { setList(r.data.reports); setCanExport(r.data.can_export); }).catch(() => setList([])); }, []);
  useEffect(() => { loadDash(); if (active) openReport(active); /* eslint-disable-next-line */ }, [preset, range]);

  const openReport = async (key) => { setActive(key); setReport(null); try { const { data } = await api.get(`/admin/reports/${key}?${qs()}`); setReport(data); } catch (e) { toast.error(apiErr(e)); setReport(false); } };
  const exportReport = async (fmt) => { try { await downloadDoc(`/admin/reports/${active}/export?format=${fmt}&${qs()}`, `the-d-${active}.${fmt}`); } catch (e) { toast.error(apiErr(e)); } };

  if (dash === null || list === null) return <Loading />;
  const fin = dash && dash.financials;
  const grouped = {};
  (list || []).forEach((r) => { (grouped[r.category] = grouped[r.category] || []).push(r); });

  return (
    <div>
      <AdminHeading title="Reports" subtitle="Booked, Collected, Outstanding, Complimentary, Waived & Refunded are tracked separately. All figures use property timezone.">
        <div className="flex gap-2 items-center flex-wrap">
          <select className={adminInput} value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="report-preset">{PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          {preset === "custom" && <><input type="date" className={adminInput} value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="report-start" /><input type="date" className={adminInput} value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="report-end" /></>}
        </div>
      </AdminHeading>
      {dash && <p className="text-xs text-muted-foreground mb-4">Range: <span className="text-gold">{dash.range_label}</span></p>}

      {fin && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-4" data-testid="financial-cards">
          {["booked", "collected", "outstanding", "complimentary", "waived", "refunded", "deposits"].map((k) => (
            <div key={k} className="bg-surface/60 border border-white/10 rounded-lg p-3" title={FIN_TIP[k]} data-testid={`fin-${k}`}>
              <p className="text-[10px] tracking-label uppercase text-muted-foreground flex items-center gap-1">{k}<Info size={9} /></p>
              <p className={`text-lg font-serif mt-1 ${k === "collected" ? "text-gold" : k === "outstanding" ? "text-red-300" : ""}`}>{money(fin[k])}</p>
            </div>
          ))}
        </div>
      )}
      {!fin && <div className="bg-gold/10 border border-gold/30 rounded-lg px-4 py-3 mb-4 text-sm text-gold">Financial figures are hidden for your role.</div>}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat label="Reservations" value={dash.cards.reservations} accent="pool" />
        <Stat label="Current Guests" value={dash.cards.current_guests} accent="gold" />
        <Stat label="Open Concierge" value={dash.cards.open_concierge} />
        <Stat label="Upcoming Events" value={dash.cards.upcoming_events} />
        <Stat label="Open Maintenance" value={dash.cards.open_maintenance} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <Panel title="Reservations Over Time">
          {dash.charts.reservations_over_time.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No data for this range.</p> : (
            <ResponsiveContainer width="100%" height={200}><BarChart data={dash.charts.reservations_over_time}><XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9aa3b5" }} /><YAxis tick={{ fontSize: 10, fill: "#9aa3b5" }} allowDecimals={false} /><Tooltip contentStyle={{ background: "#0b0e16", border: "1px solid #ffffff20" }} /><Bar dataKey="count" fill="#B7942E" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
          )}
        </Panel>
        <Panel title="Payments Collected Over Time">
          {(dash.charts.payments_over_time || []).length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No payments in range.</p> : (
            <ResponsiveContainer width="100%" height={200}><LineChart data={dash.charts.payments_over_time}><XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9aa3b5" }} /><YAxis tick={{ fontSize: 10, fill: "#9aa3b5" }} /><Tooltip contentStyle={{ background: "#0b0e16", border: "1px solid #ffffff20" }} /><Line dataKey="amount" stroke="#5AA6B8" strokeWidth={2} dot={{ r: 3 }} /></LineChart></ResponsiveContainer>
          )}
        </Panel>
        <Panel title="Reservation Status Breakdown">
          {dash.charts.by_status.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No data.</p> : (
            <ResponsiveContainer width="100%" height={200}><BarChart layout="vertical" data={dash.charts.by_status}><XAxis type="number" tick={{ fontSize: 10, fill: "#9aa3b5" }} allowDecimals={false} /><YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10, fill: "#9aa3b5" }} /><Tooltip contentStyle={{ background: "#0b0e16", border: "1px solid #ffffff20" }} /><Bar dataKey="count" fill="#5AA6B8" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>
          )}
        </Panel>
        <Panel title="Experience Popularity">
          {dash.charts.by_experience.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No data.</p> : (
            <ResponsiveContainer width="100%" height={200}><BarChart layout="vertical" data={dash.charts.by_experience}><XAxis type="number" tick={{ fontSize: 10, fill: "#9aa3b5" }} allowDecimals={false} /><YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10, fill: "#9aa3b5" }} /><Tooltip contentStyle={{ background: "#0b0e16", border: "1px solid #ffffff20" }} /><Bar dataKey="count" radius={[0, 4, 4, 0]}>{dash.charts.by_experience.map((_, i) => <Cell key={i} fill={["#B7942E", "#5AA6B8", "#8892a6", "#c9a94a"][i % 4]} />)}</Bar></BarChart></ResponsiveContainer>
          )}
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title={`All Reports (${list.length})`}>
          <div className="space-y-3 max-h-[520px] overflow-y-auto">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-[10px] tracking-label uppercase text-gold mb-1">{CAT_LABELS[cat] || cat}</p>
                {items.map((r) => <button key={r.key} onClick={() => openReport(r.key)} className={`block w-full text-left text-sm px-2 py-1.5 rounded ${active === r.key ? "bg-pool/15 text-pool" : "text-muted-foreground hover:text-foreground"}`} data-testid={`report-open-${r.key}`}>{r.label}</button>)}
              </div>
            ))}
          </div>
        </Panel>
        <div className="lg:col-span-2">
          <Panel title={report ? report.label : "Select a report"}>
            {!active ? <p className="text-sm text-muted-foreground py-8 text-center">Choose a report from the list to view records and export.</p> :
              report === null ? <Loading /> : report === false ? <p className="text-sm text-red-300">Unable to load report.</p> : (
              <>
                {canExport && (
                  <div className="flex gap-2 mb-3">
                    <button onClick={() => exportReport("csv")} className="btn-ghost-gold px-3 py-1.5 text-xs flex items-center gap-1" data-testid="report-export-csv"><FileDown size={13} /> CSV</button>
                    <button onClick={() => exportReport("pdf")} className="btn-ghost-gold px-3 py-1.5 text-xs flex items-center gap-1" data-testid="report-export-pdf"><FileDown size={13} /> PDF</button>
                    <button onClick={() => openDocPrint(`/admin/reports/${active}/export?format=pdf&${qs()}`)} className="btn-ghost-gold px-3 py-1.5 text-xs flex items-center gap-1"><Printer size={13} /> Print</button>
                  </div>
                )}
                {report.totals && <p className="text-xs text-muted-foreground mb-2">{Object.entries(report.totals).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ")}</p>}
                {report.rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No records for this range. (No fabricated data shown.)</p> : (
                  <div className="overflow-x-auto max-h-[440px]"><table className="w-full text-sm min-w-[560px]">
                    <thead><tr>{report.columns.map((c) => <Th key={c.key}>{c.label}</Th>)}{report.drill && <Th></Th>}</tr></thead>
                    <tbody>{report.rows.map((row, i) => (
                      <tr key={i}>{report.columns.map((c) => <Td key={c.key} className={c.money ? "text-right text-gold" : ""}>{c.money ? money(row[c.key]) : row[c.key]}</Td>)}
                        {report.drill && <Td>{row._id && <button onClick={() => nav(report.drill.replace("{_id}", row._id))} className="text-pool" data-testid={`report-drill-${row._id}`}><ExternalLink size={13} /></button>}</Td>}
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
